import moment, { DurationInputArg2 } from 'moment';
import { ActivityDefinition } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/ActivityDefinition';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';
import { Reference, TaskInput } from 'src/careflow/ph-core/fhir-client/resources';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { getDynamicValueExpressionByPath } from 'src/utils/fhir';
import { createAssessmentTask as createAssessmentTaskApi } from 'src/services/api/case';
import { fhirClient } from 'src/App';
import useValueSetsByIdentifiers from './useValueSetsByIdentifier';

export interface ICheckNTaskLinkerProps {
  patientId: string | null;
  activityDefinitionId: string | null;
}

export default function useCheckNTaskLinker({
  patientId,
  activityDefinitionId,
}: ICheckNTaskLinkerProps) {
  const {
    valueSets: [taskTypes, taskStatuses, taskIntents, taskPriorities],
  } = useValueSetsByIdentifiers([
    'ph-task-type-codes',
    'ph-task-status',
    'ph-task-intent',
    'ph-task-priority',
  ]);

  function getIntentFromStr(value: string | undefined) {
    if (!value) {
      return 'unknown';
    }

    switch (value) {
      case 'proposal':
        return 'proposal';
      case 'plan':
        return 'plan';
      case 'order':
        return 'order';
      case 'original-order':
        return 'original-order';
      case 'reflex-order':
        return 'reflex-order';
      case 'filler-order':
        return 'filler-order';
      case 'instance-order':
        return 'instance-order';
      case 'option':
        return 'option';
      default:
        return 'unknown';
    }
  }

  function getStatusFromStr(value: string | undefined) {
    if (!value) {
      return 'draft';
    }

    switch (value) {
      case 'draft':
        return 'draft';
      case 'requested':
        return 'requested';
      case 'received':
        return 'received';
      case 'accepted':
        return 'accepted';
      case 'rejected':
        return 'rejected';
      case 'ready':
        return 'ready';
      case 'cancelled':
        return 'cancelled';
      case 'in-progress':
        return 'in-progress';
      case 'on-hold':
        return 'on-hold';
      case 'failed':
        return 'failed';
      case 'completed':
        return 'completed';
      case 'entered-in-error':
        return 'entered-in-error';
      default:
        return 'draft';
    }
  }

  function getPriorityFromStr(value: string | undefined) {
    if (!value) {
      return undefined;
    }

    switch (value) {
      case 'routine':
        return 'routine';
      case 'urgent':
        return 'urgent';
      case 'stat':
        return 'stat';
      default:
        return 'asap';
    }
  }

  async function getActivityDefinition(activityDefinitionId: string) {
    let query = `/ActivityDefinition/${activityDefinitionId}`;
    let queryRes;

    try {
      queryRes = await fhirClient.getResource(query);
    } catch (error) {
      console.error(error);
      throw new Error('failed to get Task data to get assessment');
    }

    if (!queryRes) {
      return undefined;
    }

    return queryRes as ActivityDefinition;
  }

  function getTaskDataBasedOnActivityDefinition(activityDefinition: ActivityDefinition): Task {
    const priority = getValueSetConceptValue(
      taskPriorities?.asList?.() ?? [],
      activityDefinition?.priority
    );
    const intent = getValueSetConceptValue(
      taskIntents?.asList?.() ?? [],
      getDynamicValueExpressionByPath(activityDefinition?.dynamicValue, 'task.intent')
    );

    const status = getValueSetConceptValue(
      taskStatuses?.asList?.() ?? [],
      getDynamicValueExpressionByPath(activityDefinition?.dynamicValue, 'task.status')
    );

    let taskStartDate: moment.Moment | null = null;
    let taskEndDate: moment.Moment | null = null;

    let startDate: moment.Moment | null = moment();

    if (activityDefinition?.timingDuration?.value && activityDefinition?.timingDuration?.unit) {
      taskStartDate = startDate?.isValid?.() ? startDate : moment();
      const isNegativeDuration = activityDefinition.timingDuration.value < 0;

      if (!isNegativeDuration) {
        taskEndDate = taskStartDate
          .clone()
          .add(
            activityDefinition?.timingDuration?.value,
            activityDefinition?.timingDuration?.unit as DurationInputArg2
          );
      }
    }

    let input: TaskInput[] = [];

    if (activityDefinition.relatedArtifact?.[0]) {
      const relatedArtifact = activityDefinition.relatedArtifact?.[0];
      const rAValuableCCCoding = relatedArtifact.extension?.[0].valueCodeableConcept?.coding;

      const taskInput: TaskInput = {
        type: {
          coding: rAValuableCCCoding,
        },
        valueUrl: relatedArtifact.url,
      };

      input.push(taskInput);
    }

    let newTask: Task = {
      resourceType: 'Task',
      intent: getIntentFromStr(intent?.code),
      status: getStatusFromStr(status?.code),
      priority: getPriorityFromStr(priority?.code),
      executionPeriod: { start: startDate.toISOString(), end: taskEndDate?.toISOString() },
      description: activityDefinition.description,
      input: input,
      instantiatesCanonical: 'ActivityDefinition/' + activityDefinition.id,
      code: activityDefinition.code,
    };

    if (taskEndDate) {
      const existingExtensions = newTask.extension || [];
      let parentExtension = existingExtensions.find(
        (ext) => ext?.url === 'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail'
      );

      if (!parentExtension) {
        newTask.extension = [
          ...existingExtensions,
          {
            url: 'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-detail',
            extension: [{
              url: 'requestedPeriod',
              valuePeriod: {
                start: startDate.toISOString(),
                end: taskEndDate.toISOString(),
              },
            }],
          },
        ];
      } else {
        if (!parentExtension.extension) parentExtension.extension = [];
        parentExtension.extension.push({
          url: 'requestedPeriod',
          valuePeriod: {
            start: startDate.toISOString(),
            end: taskEndDate.toISOString(),
          },
        });
      }
    }

    return newTask;
  }

  async function createAssessmentTask(activityDefinitionId: string, baseTask?: any, basedOn?: Reference) {
    const activityId = activityDefinitionId.includes('/')
      ? activityDefinitionId.split('/').pop()
      : activityDefinitionId;

    const task: Task = {
      resourceType: 'Task',
      intent: 'order',
      status: 'requested',
      instantiatesCanonical: `ActivityDefinition/${activityId}`,
      for: {
        reference: 'Patient/' + patientId,
      },
      ...(baseTask?.description && { description: baseTask.description }),
      ...(baseTask?.owner && { owner: baseTask.owner }),
    };

    if(basedOn) {
      if(!task.basedOn) {
        task.basedOn = [];
      }
      task.basedOn.push(basedOn);
    }

    try {
      const resp = await createAssessmentTaskApi(task);
      return resp;
    } catch (err) {
      console.error('failed to create task', err);
      throw err;
    }
  }

  return { createAssessmentTask };
}
